// components/MapControls.tsx
import React, { useEffect, useRef, useState } from "react";
import type { MapViewState } from "./types/MapTypes";
import { useTranslation } from "react-i18next";

interface MapControlsProps
{
  viewport: MapViewState;
  mousePosition: [number, number];
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetNorth?: () => void;
}

const MapControls: React.FC<MapControlsProps> = ({ viewport, mousePosition, onZoomIn, onZoomOut, onResetNorth }) =>
{
  const { t } = useTranslation();

  // The coordinate bar can wrap to 2 rows on a narrow map, so its real height is
  // measured and exposed as --map-bar-h on the map frame; the right-hand button
  // clusters (zoom, compass, layers) float up with it instead of being covered.
  const barRef = useRef<HTMLDivElement>(null);
  // Very narrow map (side panel + chat open): the bar drops Z and tightens the gaps.
  const [compact, setCompact] = useState(false);

  useEffect(() =>
  {
    const bar = barRef.current;
    // Frame marked `data-map-chrome-root` if the page has one, else the direct parent.
    const host = (bar?.closest("[data-map-chrome-root]") as HTMLElement | null) ?? bar?.parentElement;
    if (!bar || !host || typeof ResizeObserver === "undefined") return;

    const apply = () =>
    {
      host.style.setProperty("--map-bar-h", `${Math.ceil(bar.getBoundingClientRect().height)}px`);
      setCompact(host.clientWidth < 460);
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    observer.observe(host);
    return () =>
    {
      observer.disconnect();
      host.style.removeProperty("--map-bar-h");
    };
  }, []);

  const btnStyle: React.CSSProperties = {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    cursor: "pointer",
    color: "rgba(255,255,255,0.7)",
    fontSize: "18px",
    transition: "all 0.15s ease",
    outline: "none",
  };

  return (
    <>
      {/* Zoom Controls */}
      <div
        style={{
          position: "absolute",
          bottom: "calc(var(--map-bar-h, 30px) + 64px)",
          right: "40px",
          zIndex: 1000,
          background: "rgba(15,17,26,0.92)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "6px",
          overflow: "hidden",
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        }}
      >
        <button
          style={btnStyle}
          onClick={onZoomIn}
          title="Zoom In"
          onMouseEnter={(e) =>
          {
            e.currentTarget.style.color = "#00d4ff";
            e.currentTarget.style.background = "rgba(0,212,255,0.1)";
          }}
          onMouseLeave={(e) =>
          {
            e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            e.currentTarget.style.background = "transparent";
          }}
        >
          +
        </button>
        <button
          style={{ ...btnStyle, borderBottom: "none" }}
          onClick={onZoomOut}
          title="Zoom Out"
          onMouseEnter={(e) =>
          {
            e.currentTarget.style.color = "#00d4ff";
            e.currentTarget.style.background = "rgba(0,212,255,0.1)";
          }}
          onMouseLeave={(e) =>
          {
            e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            e.currentTarget.style.background = "transparent";
          }}
        >
          −
        </button>
      </div>

      {/* Compass / Reset North */}
      <button
        style={{
          position: "absolute",
          bottom: "calc(var(--map-bar-h, 30px) + 144px)",
          right: "40px",
          zIndex: 1000,
          width: "36px",
          height: "36px",
          background: "rgba(15,17,26,0.92)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "6px",
          cursor: "pointer",
          color: "#ff6b6b",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          outline: "none",
          transform: `rotate(${-(viewport.bearing ?? 0)}deg)`,
          transition: "transform 0.3s ease",
        }}
        onClick={onResetNorth}
        title="Reset North"
      >
        ↑
      </button>

      {/* Coordinate / Status Bar: 2 rows x 4 columns so labels, LAT, LNG and Z line up between Center and Mouse Position. */}
      <div
        ref={barRef}
        style={{
          position: "absolute",
          bottom: "12px",
          right: "40px",
          zIndex: 1000,
          background: "rgba(15,17,26,0.88)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "8px",
          padding: "6px 12px",
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          fontSize: "10px",
          color: "rgba(255,255,255,0.5)",
          letterSpacing: "0.5px",
          display: "grid",
          gridTemplateColumns: compact ? "auto auto auto" : "auto auto auto auto",
          justifyContent: "start",
          alignItems: "center",
          columnGap: compact ? "10px" : "16px",
          rowGap: "2px",
          maxWidth: "calc(100% - 52px)",
          overflow: "hidden",
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        <span>{t("Center")}:</span>
        <span>
          <span style={{ color: "rgba(255,255,255,0.25)" }}>LAT </span>
          <span style={{ color: "#00d4ff" }}>{viewport.center[1]?.toFixed(6)}</span>
        </span>
        <span>
          <span style={{ color: "rgba(255,255,255,0.25)" }}>LNG </span>
          <span style={{ color: "#00d4ff" }}>{viewport.center[0]?.toFixed(6)}</span>
        </span>
        {!compact && (
          <span>
            <span style={{ color: "rgba(255,255,255,0.25)" }}>Z </span>
            <span style={{ color: "#7fdbff" }}>{viewport.zoom.toFixed(2)}</span>
            {viewport.bearing !== undefined && viewport.bearing !== 0 && (
              <>
                <span style={{ color: "rgba(255,255,255,0.25)" }}> BRG </span>
                <span style={{ color: "#7fdbff" }}>{viewport.bearing.toFixed(0)}°</span>
              </>
            )}
          </span>
        )}

        <span>{t("Mouse Position")}:</span>
        <span>
          <span style={{ color: "rgba(255,255,255,0.25)" }}>LAT </span>
          <span style={{ color: "#00d4ff" }}>{mousePosition[1]?.toFixed(6)}</span>
        </span>
        <span>
          <span style={{ color: "rgba(255,255,255,0.25)" }}>LNG </span>
          <span style={{ color: "#00d4ff" }}>{mousePosition[0]?.toFixed(6)}</span>
        </span>
      </div>
    </>
  );
};

export default MapControls;
